import React, { useState, useEffect } from 'react';
import { FileText, Phone, Clock, AlertCircle, Loader2, MessageCircle } from 'lucide-react';
import DOMPurify from 'dompurify';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { publicAPI } from '../services/api';
import logger from '../utils/logger';

const DownloadsPage = () => {
  const [reportForm, setReportForm] = useState({ labId: '', password: '' });
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageSettings, setPageSettings] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes, contactRes] = await Promise.all([
          publicAPI.getDownloadsPageSettings(),
          publicAPI.getContactInfo()
        ]);
        setPageSettings(settingsRes.data);
        setContactInfo(contactRes.data);
      } catch (error) {
        console.error('Error loading page data:', error);
        setPageSettings({
          hero_title: 'Download Reports',
          hero_subtitle: 'Access and download your test reports online',
          form_title: 'Download Report',
          form_subtitle: 'Access your test results online',
          help_title: 'Need Help with Reports?',
          help_text: 'If you are unable to find your report online, please contact us with your registration number and we will send the report to your registered email or WhatsApp.',
          timing_text: 'Reports can be collected from the centre between 6-8 pm',
          cta_title: 'Have Questions?',
          cta_text: 'Our team is available to help you with any queries regarding your reports or our services.'
        });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleReportSearch = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!reportForm.labId.trim() || !reportForm.password.trim()) {
      setError('Please enter both Lab ID and Mobile Number');
      return;
    }

    setReportLoading(true);

    try {
      const response = await publicAPI.searchReport(reportForm);
      
      if (response.data.success && response.data.auto_login_html) {
        // Open a new window with about:blank first
        const reportWindow = window.open('about:blank', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
        
        if (reportWindow) {
          // The auto_login_html contains a complete HTML document with a form and auto-submit script
          // We need to write it directly without sanitization since it comes from our own backend
          // and contains necessary script for auto-login functionality
          const doc = reportWindow.document;
          doc.open();
          doc.write(response.data.auto_login_html);
          doc.close();
        } else {
          setError('Please allow pop-ups to view your reports. Click the blocked pop-up icon in your browser address bar.');
        }
      } else if (!response.data.success) {
        setError(response.data.message || 'Unable to fetch reports. Please check your credentials.');
      }
    } catch (err) {
      logger.error('Report search error:', err);
      setError('Unable to connect to the report system. Please try again later.');
    } finally {
      setReportLoading(false);
    }
  };

  if (loading || !pageSettings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#007897]" />
      </div>
    );
  }

  const primaryPhone = '011-47500500';
  const whatsappNumber = contactInfo?.whatsapp || '+919953852212';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#007897] to-[#005a73]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{pageSettings.hero_title}</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              {pageSettings.hero_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Report Download Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-[#007897] to-[#005a73] rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{pageSettings.form_title}</h2>
                  <p className="text-white/80 text-sm">{pageSettings.form_subtitle}</p>
                </div>
              </div>

              <form onSubmit={handleReportSearch} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-2">
                    Lab ID <span className="text-white/60">(as printed on your receipt)</span>
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="Enter your Lab ID"
                      value={reportForm.labId}
                      onChange={(e) => setReportForm({...reportForm, labId: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 bg-white text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/50"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/90 mb-2">
                    Password <span className="text-white/60">(your registered mobile number)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="Enter your mobile number"
                      value={reportForm.password}
                      onChange={(e) => setReportForm({...reportForm, password: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 bg-white text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/50"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-400/30 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={reportLoading}
                  className="w-full bg-white text-[#007897] py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {reportLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Loading Reports...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5" />
                      View My Reports
                    </>
                  )}
                </button>
              </form>

              <p className="text-xs text-white/60 mt-4 text-center">
                Your reports will open in a new window. After login, click on your report and use the Print option to download as PDF.
              </p>
            </div>

            {/* Contact for Reports */}
            <div className="mt-6 bg-amber-50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{pageSettings.help_title}</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    {pageSettings.help_text}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a 
                      href={`tel:${primaryPhone}`}
                      className="inline-flex items-center gap-2 text-sm text-[#007897] font-medium hover:underline"
                    >
                      <Phone size={14} />
                      {primaryPhone}
                    </a>
                    <a 
                      href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Hello, I need help with my test report.')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-green-600 font-medium hover:underline"
                    >
                      <MessageCircle size={14} />
                      WhatsApp: {whatsappNumber}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Report Timing Info */}
            <div className="mt-4 bg-gray-100 rounded-xl p-4">
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <Clock size={16} className="text-gray-500" />
                <span className="font-bold uppercase">{pageSettings.timing_text}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{pageSettings.cta_title}</h2>
          <p className="text-gray-600 mb-8">
            {pageSettings.cta_text}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#007897] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#006680] transition-colors"
            >
              <Phone size={20} />
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DownloadsPage;
